/**
 * Words for an answer receipt. Pure, so the panel stays a thin rendering.
 */
import { memberLabel } from "../../../services/chartOptions";
import type {
  CheckedFigure,
  QueryReceipt,
  ReceiptFilter,
  ReceiptJoin,
  ReceiptOrder,
  ReceiptPeriod,
} from "../../../services/answerReceipt";

const OPERATORS: Record<string, string> = {
  equals: "is",
  notEquals: "is not",
  contains: "contains",
  notContains: "does not contain",
  startsWith: "starts with",
  endsWith: "ends with",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  set: "is set",
  notSet: "is not set",
  inDateRange: "between",
  beforeDate: "before",
  afterDate: "after",
};

const CARDINALITIES: Record<string, string> = {
  many_to_one: "many-to-one",
  one_to_many: "one-to-many",
  one_to_one: "one-to-one",
  many_to_many: "many-to-many",
};

function entityLabel(entity: string): string {
  return entity
    .replace(/^(dim|fact|fct|stg)_/i, "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function measureLabel(receipt: QueryReceipt, member: string): string {
  const unit = receipt.units[member];
  return unit ? `${memberLabel(member)} (${unit})` : memberLabel(member);
}

export function describeFilter(filter: ReceiptFilter): string {
  const operator = OPERATORS[filter.operator] ?? filter.operator;
  if (!filter.values.length) return `${memberLabel(filter.member)} ${operator}`;
  const joiner = filter.operator === "inDateRange" ? " and " : ", ";
  return `${memberLabel(filter.member)} ${operator} ${filter.values.join(joiner)}`;
}

export function describePeriod(period: ReceiptPeriod): string {
  const range = Array.isArray(period.dateRange)
    ? period.dateRange.join(" to ")
    : period.dateRange;
  const parts = [memberLabel(period.dimension)];
  if (period.granularity) parts.push(`by ${period.granularity}`);
  parts.push(range ? `from ${range}` : "all dates");
  return parts.join(", ");
}

export function describeJoin(join: ReceiptJoin): string {
  const cardinality = CARDINALITIES[join.cardinality] ?? join.cardinality;
  return `${entityLabel(join.fromEntity)} → ${entityLabel(join.toEntity)} (${cardinality})`;
}

export function describeOrder(order: ReceiptOrder): string {
  return `${memberLabel(order.member)}, ${order.direction === "desc" ? "largest first" : "smallest first"}`;
}

export function describeRows(receipt: QueryReceipt): string {
  const rows = receipt.rowCount.toLocaleString();
  const limit = receipt.rowLimit ? ` of at most ${receipt.rowLimit.toLocaleString()}` : "";
  return `${rows}${limit}${receipt.truncated ? " (row limit reached; more rows exist)" : ""}`;
}

/** Every distinct unfinished-period note across an answer's queries. */
export function answerPeriodNotes(receipts: QueryReceipt[]): string[] {
  return [...new Set(receipts.flatMap((item) => item.periodNotes))];
}

const STANDING: Record<string, number> = {
  certified: 3,
  approved_with_exceptions: 2,
  promoted: 1,
};

/**
 * The weakest endorsement among an answer's queries, as shown beside the
 * verdict. An answer is only as governed as its least endorsed source, so one
 * promoted query makes the whole answer "not certified".
 */
export function answerEndorsementLabel(receipts: QueryReceipt[]): string | null {
  if (!receipts.length) return null;
  const weakest = receipts.reduce((low, item) =>
    (STANDING[item.endorsement ?? ""] ?? 0) < (STANDING[low.endorsement ?? ""] ?? 0) ? item : low);
  return endorsementLabel(weakest);
}

export function endorsementLabel(receipt: QueryReceipt): string {
  if (receipt.endorsement === "certified") return "Certified";
  if (receipt.endorsement === "promoted") return "Promoted, not certified";
  if (receipt.endorsement === "approved_with_exceptions") {
    return "Approved with exceptions, not certified";
  }
  return "Not endorsed";
}

/** Where a verified figure came from, such as "share of Total Amount". */
export function describeMatch(figure: CheckedFigure): string {
  const match = figure.match;
  if (!match) return "No governed query produced this figure.";
  const member = match.member ? memberLabel(match.member) : "";
  const source = {
    value: `${member} as returned`,
    total: `total of ${member}`,
    average: `average of ${member}`,
    minimum: `lowest ${member}`,
    maximum: `highest ${member}`,
    share: `share of total ${member}`,
    difference: `difference in ${member}`,
    change: `change in ${member}`,
    row_count: "number of rows returned",
  }[match.kind];
  return figure.approximate ? `${source}, approximately` : source;
}
